import "./CampaignMappingLabeling.scss";
import cn from "classnames";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { RouteComponentProps } from "@reach/router";
import { Nav } from "react-bootstrap";
import { MdDelete, MdErrorOutline, MdSave } from "react-icons/md";
import { useSelector } from "react-redux";
import { formatNumber } from "@blisspointmedia/bpm-types/dist/formatUtils";
import { Button, ButtonType } from "../Components/Button/Button";
import { FilterBar } from "../Components/FilterBar/FilterBar";
import { FullPageSpinner } from "../Components/Spinner";
import { ButtonFrameworkVariant } from "../Components/ButtonFramework/ButtonFramework";
import AlertDialog, { AlertDialogVariants } from "../Components/AlertDialog/AlertDialog";
import { useCompanyInfo } from "../redux/company";
import { useSetError } from "../redux/modals";
import * as UserRedux from "../redux/user";
import { SegmentationMappingLambdaFetch } from "../utils/fetch-utils";
import { useStateFunction } from "../utils/hooks/useData";
import { Brand60 } from "../utils/colors";
import CampaignMappingPendingChanges from "./CampaignMappingPendingChanges";
import CampaignMappingTable from "./CampaignMappingTable";
import {
  buildSavePayloadItem,
  editClearedSlots,
  editHasChanges,
  getLevelLabel,
} from "./CampaignMappingUtils";
import {
  CampaignEdit,
  CampaignEditsMap,
  CampaignMappingRow,
  CampaignSavePayload,
  CampaignSavePayloadItem,
  CampaignSelectedRowsMap,
  MappingLevel,
  MappingOption,
} from "@blisspointmedia/bpm-types/dist/CampaignMappingTool";

interface PendingClear {
  rawDataId: string;
  rowLabel: string;
  levels: MappingLevel[];
}

interface CampaignMappingLabelingProps {
  data: CampaignMappingRow[] | null;
  mappingOptions: Record<MappingLevel, MappingOption[]>;
  fetchRawData: () => Promise<void>;
  setHasUnsavedChanges: (value: boolean) => void;
}

enum TabOptions {
  LABELLED = "Labelled",
  UNLABELLED = "Unlabelled",
}

const CampaignMappingLabeling: React.FC<CampaignMappingLabelingProps & RouteComponentProps> = ({
  data,
  mappingOptions,
  fetchRawData,
  setHasUnsavedChanges,
}) => {
  const setError = useSetError();
  const { cid } = useCompanyInfo();
  const userEmail = useSelector(UserRedux.emailSelector);
  const [activeTab, setActiveTab] = useState<TabOptions>(TabOptions.UNLABELLED);
  const [saving, setSaving] = useState(false);
  const [editsMap, setEditsMap] = useState<CampaignEditsMap>({});
  const [selectedRows, setSelectedRows] = useState<CampaignSelectedRowsMap>({});
  const [showPendingChanges, setShowPendingChanges] = useState(false);
  const [pendingClears, setPendingClears] = useState<{
    items: CampaignSavePayloadItem[];
    clears: PendingClear[];
  } | null>(null);
  const [dialog, setDialog] = useState<{
    show: boolean;
    title: string;
    body: string;
    variant?: AlertDialogVariants;
  } | null>(null);
  const dataByRawDataId = useMemo(() => {
    const next: Record<string, CampaignMappingRow> = {};
    (data || []).forEach(row => {
      next[row.raw_data_id] = row;
    });
    return next;
  }, [data]);

  const filterBarOptions = useMemo(
    () => [
      { name: "status", label: "Status" },
      { name: "analytics_raw_source", label: "Analytics Raw Source" },
      { name: "analytics_raw_medium", label: "Analytics Raw Medium" },
      { name: "analytics_campaign_name", label: "Analytics Campaign Name" },
      { name: "analytics_campaign_id", label: "Analytics Campaign ID" },
      { name: "analytics_ad_group_name", label: "Analytics Ad Group" },
      { name: "analytics_ad_group_id", label: "Analytics Ad Group ID" },
      { name: "analytics_ad_name", label: "Analytics Ad" },
      { name: "analytics_ad_id", label: "Analytics Ad ID" },
    ],
    []
  );

  const [filter, setFilter] = useStateFunction<(line: CampaignMappingRow) => boolean>(() => true);

  const unlabelledData = useMemo(() => (data || []).filter(row => row.status === "unlabelled"), [
    data,
  ]);
  const labelledData = useMemo(() => (data || []).filter(row => row.status === "labelled"), [data]);

  const dataToUse = activeTab === TabOptions.UNLABELLED ? unlabelledData : labelledData;

  const filteredData = useMemo(() => dataToUse.filter(filter), [dataToUse, filter]);

  const selectedRowCount = Object.keys(selectedRows).length;

  const selectAllFilteredRows = useCallback(() => {
    setSelectedRows(current => {
      const next = { ...current };
      for (const row of filteredData) {
        next[row.raw_data_id] = row;
      }
      return next;
    });
  }, [filteredData]);

  const clearSelection = useCallback(() => setSelectedRows({}), []);

  const hasPendingChanges = Object.keys(editsMap).length > 0;

  useEffect(() => {
    setHasUnsavedChanges(hasPendingChanges);
  }, [hasPendingChanges, setHasUnsavedChanges]);

  const onSelectMapping = useCallback(
    (row: CampaignMappingRow, level: MappingLevel, option: MappingOption | null) => {
      setEditsMap(current => {
        const targets = selectedRows[row.raw_data_id] ? Object.values(selectedRows) : [row];
        const next = { ...current };

        for (const target of targets) {
          const prev = next[target.raw_data_id];
          const merged: CampaignEdit = {
            ...(prev || { rawDataId: target.raw_data_id }),
            rawDataId: target.raw_data_id,
            [level]: option,
          };

          if (editHasChanges(target, merged)) {
            next[target.raw_data_id] = merged;
          } else {
            delete next[target.raw_data_id];
          }
        }

        return next;
      });
    },
    [selectedRows]
  );

  const discardChanges = useCallback(() => {
    setEditsMap({});
    setSelectedRows({});
    setShowPendingChanges(false);
  }, []);

  const onRemoveEdit = useCallback((rawDataId: string) => {
    setEditsMap(current => {
      const next = { ...current };
      delete next[rawDataId];
      return next;
    });
  }, []);

  const executeSave = useCallback(
    async (mappings: CampaignSavePayloadItem[]) => {
      if (!mappings.length) {
        setEditsMap({});
        setSelectedRows({});
        return;
      }

      const body: CampaignSavePayload = { company: cid, user: userEmail, mappings };

      try {
        setSaving(true);
        await SegmentationMappingLambdaFetch("/updateManualCampaignNameMapping", {
          method: "POST",
          body,
        });

        setDialog({
          show: true,
          title: "Campaign mappings submitted",
          body: `Your ${mappings.length} campaign mapping change${
            mappings.length === 1 ? "" : "s"
          } are being saved. You'll receive an email at ${userEmail} when processing completes.`,
          variant: AlertDialogVariants.SUCCESS,
        });

        setEditsMap({});
        setSelectedRows({});
        setPendingClears(null);
        await fetchRawData();
      } catch (error) {
        const reportError = error as Error;
        setError({ message: reportError.message, reportError });
      } finally {
        setSaving(false);
      }
    },
    [cid, fetchRawData, setError, userEmail]
  );

  const saveChanges = useCallback(() => {
    const mappings: CampaignSavePayloadItem[] = [];
    const clears: PendingClear[] = [];
    const fullyUnmapped: string[] = [];

    for (const edit of Object.values(editsMap)) {
      const row = dataByRawDataId[edit.rawDataId];
      if (!row) {
        continue;
      }
      const rowLabel = row.analytics_campaign_name || row.analytics_campaign_id || edit.rawDataId;
      const payloadItem = buildSavePayloadItem(row, edit);
      if (!payloadItem) {
        fullyUnmapped.push(rowLabel);
        continue;
      }
      mappings.push(payloadItem);

      const clearedLevels = editClearedSlots(row, edit);
      if (clearedLevels.length) {
        clears.push({ rawDataId: edit.rawDataId, rowLabel, levels: clearedLevels });
      }
    }

    if (fullyUnmapped.length) {
      setDialog({
        show: true,
        title: "Cannot fully unmap rows",
        body: `Saving with no mapping at any level isn't supported. Restore a mapping or discard the edit on: ${fullyUnmapped.join(
          ", "
        )}.`,
        variant: AlertDialogVariants.ERROR,
      });
      return;
    }

    if (clears.length) {
      setPendingClears({ items: mappings, clears });
      return;
    }

    executeSave(mappings);
  }, [dataByRawDataId, editsMap, executeSave]);

  return (
    <div className="campaignMappingLabeling">
      <Nav
        activeKey={activeTab}
        onSelect={value => {
          const selectedTab = value as TabOptions;
          if (!selectedTab || selectedTab === activeTab) {
            return;
          }
          setActiveTab(selectedTab);
          setSelectedRows({});
        }}
      >
        <Nav.Item key={TabOptions.UNLABELLED}>
          <Nav.Link eventKey={TabOptions.UNLABELLED}>
            {TabOptions.UNLABELLED} ({formatNumber(unlabelledData.length)})
          </Nav.Link>
        </Nav.Item>
        <Nav.Item key={TabOptions.LABELLED}>
          <Nav.Link eventKey={TabOptions.LABELLED}>
            {TabOptions.LABELLED} ({formatNumber(labelledData.length)})
          </Nav.Link>
        </Nav.Item>
      </Nav>

      <div className="campaignMappingLabelingBody">
        {data ? (
          <>
            <div className="filterBarContainer">
              <FilterBar
                options={filterBarOptions}
                lines={dataToUse}
                onFilter={setFilter}
                hasAdvanced
              />
            </div>

            {selectedRowCount > 0 && (
              <div className="bulkSelectBanner">
                <span>
                  {formatNumber(selectedRowCount)} row{selectedRowCount === 1 ? "" : "s"} selected.
                </span>
                {selectedRowCount < filteredData.length ? (
                  <span className="bulkSelectAction" onClick={selectAllFilteredRows}>
                    Select all {formatNumber(filteredData.length)} filtered rows
                  </span>
                ) : (
                  <span className="bulkSelectConfirm">
                    All {formatNumber(filteredData.length)} filtered rows selected.
                  </span>
                )}
                <span className="bulkSelectAction" onClick={clearSelection}>
                  Clear selection
                </span>
              </div>
            )}

            <div className="campaignMappingTableContainer">
              <CampaignMappingTable
                data={filteredData}
                editsMap={editsMap}
                mappingOptions={mappingOptions}
                onSelectMapping={onSelectMapping}
                selectedRows={selectedRows}
                setSelectedRows={setSelectedRows}
              />
            </div>
          </>
        ) : (
          <FullPageSpinner />
        )}

        <div className="campaignMappingControls">
          <div className="pendingChangesControls">
            <div className="unsavedChanges">
              <span className="counterText">
                <span className={cn({ unsavedCount: hasPendingChanges })}>
                  {formatNumber(labelledData.length + Object.keys(editsMap).length)}
                </span>
                /{formatNumber((data || []).length)} Rows Labelled
              </span>
              {hasPendingChanges && (
                <Button
                  type={ButtonType.OUTLINED}
                  variant={ButtonFrameworkVariant.LEADING_ICON}
                  design="secondary"
                  className="danger"
                  icon={<MdErrorOutline />}
                  onClick={() => setShowPendingChanges(prev => !prev)}
                >
                  {Object.keys(editsMap).length} Unsaved Change
                  {Object.keys(editsMap).length === 1 ? "" : "s"}
                </Button>
              )}
            </div>

            {hasPendingChanges && (
              <>
                <Button
                  type={ButtonType.OUTLINED}
                  variant={ButtonFrameworkVariant.LEADING_ICON}
                  onClick={discardChanges}
                  icon={<MdDelete color={Brand60} />}
                  disabled={saving}
                >
                  Discard Changes
                </Button>
                <Button
                  type={ButtonType.FILLED}
                  variant={ButtonFrameworkVariant.LEADING_ICON}
                  onClick={saveChanges}
                  icon={<MdSave />}
                  disabled={saving}
                >
                  Save
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {dialog && (
        <AlertDialog
          show={dialog.show}
          onHide={() => setDialog(null)}
          icon={null}
          title={dialog.title}
          body={dialog.body}
          primaryButtonLabel="Close"
          variant={dialog.variant}
        />
      )}

      {showPendingChanges && hasPendingChanges && (
        <CampaignMappingPendingChanges
          allRows={data || []}
          editsMap={editsMap}
          dataByRawDataId={dataByRawDataId}
          mappingOptions={mappingOptions}
          onRemoveEdit={onRemoveEdit}
          onClose={() => setShowPendingChanges(false)}
        />
      )}

      {pendingClears && (
        <AlertDialog
          show
          icon={<MdErrorOutline color={Brand60} size={48} />}
          title="Remove existing mappings?"
          body={
            <div className="campaignClearConfirm">
              <div>The following mappings will be removed:</div>
              <ul>
                {pendingClears.clears.map(clear => (
                  <li key={clear.rawDataId}>
                    <strong>{clear.rowLabel}</strong>: {clear.levels.map(getLevelLabel).join(", ")}
                  </li>
                ))}
              </ul>
              <div>Empty values overwrite existing mappings on every matched row. Continue?</div>
            </div>
          }
          variant={AlertDialogVariants.ERROR}
          primaryButtonLabel="Cancel"
          secondaryButtonLabel="Save anyway"
          onHide={() => setPendingClears(null)}
          secondaryAction={() => executeSave(pendingClears.items)}
        />
      )}
    </div>
  );
};

export default CampaignMappingLabeling;
